import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { useCrmApi } from '../../../common/hooks/use-crm-api.hook';
import { CrmBoardKind } from '../../../common/models/crm.model';

type BoardRecord = { _id: string; stage: string; sort: number };

/**
 * Load and edit the records of one Kanban kind (partners, investors, press):
 * optimistic stage moves and reorders that roll back if the API refuses.
 * `savedKey` is the i18n key of the toast shown on save.
 */
export function useCrmBoard<T extends BoardRecord>(kind: Exclude<CrmBoardKind, 'leads'>, savedKey: string) {
  const { t } = useTranslation();
  const api = useCrmApi();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setItems((await api.list<T>(kind)) || []);
    } catch {
      toast.error(t('ADMIN_CRM.COMMON.LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  /** Create or update; resolves true when saved so the caller can close its sheet. */
  const save = async (data: Partial<T>): Promise<boolean> => {
    try {
      const { _id, ...fields } = data;
      if (_id) {
        const updated = await api.update<T>(kind, _id, fields as Partial<T>);
        setItems((xs) => xs.map((x) => (x._id === _id ? { ...x, ...updated } : x)));
      } else {
        const created = await api.create<T>(kind, fields as Partial<T>);
        setItems((xs) => [...xs, created]);
      }
      toast.success(t(savedKey));
      return true;
    } catch (e: any) {
      toast.error(e?.message || t('ADMIN_CRM.COMMON.SAVE_FAILED'));
      return false;
    }
  };

  const remove = async (id: string): Promise<boolean> => {
    try {
      await api.remove(kind, id);
      setItems((xs) => xs.filter((x) => x._id !== id));
      return true;
    } catch {
      toast.error(t('ADMIN_CRM.COMMON.DELETE_FAILED'));
      return false;
    }
  };

  const moveStage = async (id: string, stage: string) => {
    const prev = items.find((x) => x._id === id)?.stage;
    setItems((xs) => xs.map((x) => (x._id === id ? { ...x, stage } : x)));
    try {
      await api.update<T>(kind, id, { stage } as Partial<T>);
    } catch {
      setItems((xs) => xs.map((x) => (x._id === id && prev ? { ...x, stage: prev } : x)));
      toast.error(t('ADMIN_CRM.COMMON.MOVE_FAILED'));
    }
  };

  const reorder = async (updates: { id: string; sort: number; stage?: string }[]) => {
    const before = items;
    setItems((xs) => xs.map((x) => {
      const u = updates.find((n) => n.id === x._id);
      return u ? { ...x, sort: u.sort, ...(u.stage ? { stage: u.stage } : {}) } : x;
    }));
    try {
      await api.reorder(kind, updates);
    } catch {
      setItems(before);
      toast.error(t('ADMIN_CRM.COMMON.REORDER_FAILED'));
    }
  };

  return { items, loading, save, remove, moveStage, reorder };
}
